import {
  PageShell,
} from "@/shared/components/pageShell";
/**
 * InventoryCatalogueView — browse ops-volume SKUs used for quote matching & pricing.
 * v1 read-only; catalogue JSON remains source of truth until edit workflow is added.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { CopyOutlined, ReloadOutlined, SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  fetchInventoryCatalogueMeta,
  listInventoryCatalogueItems,
  resolveInventoryCatalogueItem,
} from "../services/inventoryCatalogueService.js";
import {
  formatVolumeMetricTooltip,
  getVolumeMetricHelp,
} from "../../quotes/utils/volumeMetricHelp.js";

const { Title, Text, Paragraph } = Typography;

const formatSectionLabel = (section) =>
  String(section || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const VolumeColumnTitle = ({ metric, label }) => (
  <Space size={4}>
    {label}
    <Tooltip
      title={
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {getVolumeMetricHelp(metric).title}
          </div>
          <div style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>
            {formatVolumeMetricTooltip(metric)}
          </div>
        </div>
      }
    >
      <InfoCircleOutlined style={{ fontSize: 12, color: "#8c8c8c", cursor: "help" }} />
    </Tooltip>
  </Space>
);

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success(`${label} copied`);
  } catch {
    message.error("Could not copy to clipboard");
  }
};

export default function InventoryCatalogueView() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [resolveInput, setResolveInput] = useState({
    description: "",
    section: "",
    sizeVariant: "",
  });
  const [resolveResult, setResolveResult] = useState(null);
  const [resolveLoading, setResolveLoading] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadCatalogue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metaData, listData] = await Promise.all([
        fetchInventoryCatalogueMeta(),
        listInventoryCatalogueItems({
          section: sectionFilter || undefined,
          q: debouncedSearch || undefined,
          limit: 300,
        }),
      ]);
      setMeta(metaData);
      setItems(listData?.items || []);
      const uniqueSections = [
        ...new Set((listData?.items || []).map((item) => item.section)),
      ].sort();
      setSections(uniqueSections);
    } catch (err) {
      setError(err.message || "Failed to load inventory catalogue");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sectionFilter]);

  useEffect(() => {
    loadCatalogue();
  }, [loadCatalogue]);

  const sectionOptions = useMemo(() => {
    const fromItems = sections.map((section) => ({
      value: section,
      label: formatSectionLabel(section),
    }));
    if (sectionFilter && !sections.includes(sectionFilter)) {
      return [
        { value: sectionFilter, label: formatSectionLabel(sectionFilter) },
        ...fromItems,
      ];
    }
    return fromItems;
  }, [sections, sectionFilter]);

  const handleResolve = async () => {
    const description = resolveInput.description.trim();
    if (!description) {
      message.warning("Enter a description to test matching");
      return;
    }
    setResolveLoading(true);
    try {
      const result = await resolveInventoryCatalogueItem({
        description,
        section: resolveInput.section || undefined,
        sizeVariant: resolveInput.sizeVariant || undefined,
      });
      setResolveResult(result);
    } catch (err) {
      message.error(err.message || "Resolve failed");
      setResolveResult(null);
    } finally {
      setResolveLoading(false);
    }
  };

  const columns = [
    {
      title: "Display name",
      dataIndex: "displayName",
      key: "displayName",
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value}</Text>
          {record.sizeVariant ? (
            <Text type="secondary">{record.sizeVariant}</Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Section",
      dataIndex: "section",
      key: "section",
      width: 160,
      render: (value) => <Tag>{formatSectionLabel(value)}</Tag>,
    },
    {
      title: <VolumeColumnTitle metric="ops" label="Ops m³" />,
      dataIndex: "opsM3",
      key: "opsM3",
      width: 90,
      render: (value) => (value != null ? Number(value).toFixed(2) : "—"),
    },
    {
      title: <VolumeColumnTitle metric="billable" label="Billable m³" />,
      dataIndex: "billableM3",
      key: "billableM3",
      width: 110,
      render: (value) => (value != null ? Number(value).toFixed(2) : "—"),
    },
    {
      title: "SKU ID",
      dataIndex: "skuId",
      key: "skuId",
      width: 220,
      render: (value) => (
        <Space>
          <Text code>{value}</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            aria-label={`Copy SKU ${value}`}
            onClick={() => copyToClipboard(value, "SKU ID")}
          />
        </Space>
      ),
    },
    {
      title: "Lead label",
      key: "leadLabel",
      width: 260,
      render: (_, record) => {
        const label = record.sizeVariant
          ? `${record.displayName} (${record.sizeVariant})`
          : record.displayName;
        return (
          <Space>
            <Text>{label}</Text>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              aria-label={`Copy label ${label}`}
              onClick={() => copyToClipboard(label, "Lead label")}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Inventory catalogue
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Browse the ops-volume SKU list used for automated quote matching and pricing.
            Copy exact lead labels when fixing catalogue match blockers on quotes.
          </Paragraph>
        </div>

        {meta ? (
          <Alert
            type="info"
            showIcon
            message={
              <Space wrap>
                <Text>
                  Version: <Text code>{meta.catalogueVersion}</Text>
                </Text>
                <Text>
                  Active SKUs: <Text strong>{meta.itemCount}</Text>
                </Text>
              </Space>
            }
          />
        ) : null}

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <Card title="Match tester" size="small">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Test how a customer description resolves before updating lead inventory.
            </Paragraph>
            <Space wrap style={{ width: "100%" }}>
              <Input
                placeholder="e.g. Dining Table (6-seater)"
                value={resolveInput.description}
                onChange={(event) =>
                  setResolveInput((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                style={{ minWidth: 280 }}
              />
              <Select
                allowClear
                placeholder="Section hint"
                value={resolveInput.section || undefined}
                onChange={(value) =>
                  setResolveInput((prev) => ({ ...prev, section: value || "" }))
                }
                options={sectionOptions}
                style={{ minWidth: 180 }}
              />
              <Input
                placeholder="Size variant (optional)"
                value={resolveInput.sizeVariant}
                onChange={(event) =>
                  setResolveInput((prev) => ({
                    ...prev,
                    sizeVariant: event.target.value,
                  }))
                }
                style={{ width: 160 }}
              />
              <Button type="primary" loading={resolveLoading} onClick={handleResolve}>
                Test match
              </Button>
            </Space>
            {resolveResult ? (
              <Alert
                type={
                  resolveResult.status === "unique"
                    ? "success"
                    : resolveResult.suggestions?.length
                      ? "warning"
                      : "error"
                }
                showIcon
                message={resolveResult.hint || resolveResult.status}
                description={
                  resolveResult.matchedSku ? (
                    <Text>
                      Matched: {resolveResult.matchedSku.displayName} (
                      {resolveResult.matchedSku.skuId})
                    </Text>
                  ) : resolveResult.suggestions?.length ? (
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      {resolveResult.suggestions.slice(0, 5).map((item) => (
                        <li key={item.skuId}>
                          {item.displayName}
                          {item.sizeVariant ? ` (${item.sizeVariant})` : ""} — {item.skuId}
                        </li>
                      ))}
                    </ul>
                  ) : null
                }
              />
            ) : null}
          </Space>
        </Card>

        <Card
          title="Browse SKUs"
          extra={
            <Button icon={<ReloadOutlined />} onClick={loadCatalogue} loading={loading}>
              Refresh
            </Button>
          }
        >
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search display name or alias"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ minWidth: 280 }}
            />
            <Select
              allowClear
              placeholder="Filter by section"
              value={sectionFilter || undefined}
              onChange={(value) => setSectionFilter(value || "")}
              options={sectionOptions}
              style={{ minWidth: 200 }}
            />
          </Space>

          <Table
            rowKey="skuId"
            loading={loading}
            columns={columns}
            dataSource={items}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: 960 }}
          />
        </Card>
      </Space>
    </PageShell>
  );
}
